import React, { useState, useEffect } from 'react';
import { X, Printer } from 'lucide-react';

// 시판 라벨지 프리셋 (A4 기준, mm) — 값은 아래 입력칸에서 직접 수정 가능
const PRESETS = {
    form8: { name: '8칸 (99.1×67.7mm · 폼텍 3108 호환)', cols: 2, rows: 4, w: 99.1, h: 67.7, mt: 13.1, ml: 5.9, gx: 0, gy: 0 },
    form12: { name: '12칸 (99.1×42.3mm)', cols: 2, rows: 6, w: 99.1, h: 42.3, mt: 21.6, ml: 5.9, gx: 0, gy: 0 },
    form24: { name: '24칸 (63.5×33.9mm · 폼텍 3100 호환)', cols: 3, rows: 8, w: 63.5, h: 33.9, mt: 12.9, ml: 7.25, gx: 2.5, gy: 0 },
    custom: { name: '직접 입력', cols: 2, rows: 4, w: 99.1, h: 67.7, mt: 13.1, ml: 5.9, gx: 0, gy: 0 },
};

const STORAGE_KEY = 'labelPrintSettings';

const escapeHtml = (s) => String(s || '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

function LabelPrintModal({ items, onClose, onPrinted }) {
    // items: [{ patientName, chartNumber, clinicName, prosthesisLines, shadeText, dueText }, ...]
    const [preset, setPreset] = useState('form8');
    const [geo, setGeo] = useState({ ...PRESETS.form8 });
    const [startCell, setStartCell] = useState(0);
    const [copies, setCopies] = useState(1); // 환자당 매수
    const [fields, setFields] = useState({ clinic: true, prosthesis: true, shade: true, due: true });

    // 마지막 설정 복원 (라벨지 규격은 매번 같으므로)
    useEffect(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
            if (saved?.geo) {
                setPreset(saved.preset || 'custom');
                setGeo(saved.geo);
                if (saved.fields) setFields(saved.fields);
            }
        } catch (e) { /* 저장값 없음 */ }
    }, []);

    const selectPreset = (key) => {
        setPreset(key);
        if (key !== 'custom') setGeo({ ...PRESETS[key] });
        setStartCell(0);
    };

    const updateGeo = (key, value) => {
        setPreset('custom');
        setGeo(prev => ({ ...prev, [key]: Number(value) || 0 }));
    };

    const totalCells = geo.cols * geo.rows;
    const safeStart = Math.min(startCell, totalCells - 1);
    const copiesNum = Math.max(1, Number(copies) || 1);
    const totalLabels = items.length * copiesNum;

    const buildLines = (item) => {
        const lines = [];
        if (fields.prosthesis) item.prosthesisLines.forEach(l => lines.push(l));
        if (fields.shade && item.shadeText && item.shadeText !== '-') lines.push('쉐이드 ' + item.shadeText);
        if (fields.due && item.dueText && item.dueText !== '-') lines.push('납기 ' + item.dueText);
        return lines;
    };

    const handlePrint = () => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ preset, geo, fields }));
        } catch (e) { /* 저장 실패는 무시 */ }

        const { cols, rows, w, h, mt, ml, gx, gy } = geo;
        const cells = cols * rows;
        const scale = Math.max(0.45, Math.min(1, Math.min(h / 67.7, w / 99.1)));
        const pad = Math.max(1.5, 3 * scale);

        // 내용은 .fit 래퍼에 담고, 인쇄 직전 칸 크기에 맞춰 글자 크기를 자동 확대/축소
        const cellInnerFor = (item) => {
            const lines = buildLines(item);
            return `<div class="fit"><div class="nm">${escapeHtml(item.patientName || '-')}` +
                (item.chartNumber ? `<span class="ch"> ${escapeHtml(item.chartNumber)}</span>` : '') +
                `</div>` +
                (fields.clinic && item.clinicName ? `<div class="cl">${escapeHtml(item.clinicName)}</div>` : '') +
                lines.map(l => `<div class="ln">${escapeHtml(l)}</div>`).join('') +
                `</div>`;
        };

        // 라벨 순서: 환자1×매수, 환자2×매수, ... 시작 칸부터 연속 배치
        const labelList = [];
        items.forEach(item => {
            for (let c = 0; c < copiesNum; c++) labelList.push(item);
        });

        const pages = Math.ceil((safeStart + labelList.length) / cells);
        let pagesHtml = '';
        for (let p = 0; p < pages; p++) {
            let cellsHtml = '';
            for (let i = 0; i < cells; i++) {
                const idx = p * cells + i;
                const labelIdx = idx - safeStart;
                if (labelIdx < 0 || labelIdx >= labelList.length) continue;
                const col = i % cols;
                const row = Math.floor(i / cols);
                const left = (ml + col * (w + gx)).toFixed(2);
                const top = (mt + row * (h + gy)).toFixed(2);
                cellsHtml += `<div class="cell" style="left:${left}mm;top:${top}mm;">${cellInnerFor(labelList[labelIdx])}</div>`;
            }
            pagesHtml += `<div class="page">${cellsHtml}</div>`;
        }

        const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
            @page { size: A4; margin: 0; }
            html, body { margin: 0; padding: 0; }
            body { font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; color: #000; }
            .page { position: relative; width: 210mm; height: 296mm; page-break-after: always; overflow: hidden; }
            .page:last-child { page-break-after: auto; }
            .cell { position: absolute; width: ${w}mm; height: ${h}mm; padding: ${pad}mm; box-sizing: border-box; overflow: hidden; }
            .fit { overflow-wrap: anywhere; }
            .nm { font-size: 1.6em; font-weight: 700; line-height: 1.15; margin-bottom: 0.15em; }
            .ch { font-size: 0.95em; font-weight: 400; color: #333; }
            .cl { font-size: 1em; font-weight: 600; margin-bottom: 0.1em; }
            .ln { font-size: 0.95em; line-height: 1.35; }
        </style></head><body>${pagesHtml}<script>
            // 각 라벨 칸의 글자 크기를 칸이 꽉 차도록 자동 조절 (이진 탐색)
            document.querySelectorAll('.cell').forEach(function (cell) {
                var fit = cell.firstElementChild;
                if (!fit) return;
                var cs = getComputedStyle(cell);
                var innerH = cell.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
                var innerW = cell.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
                var lo = 5, hi = 72;
                for (var i = 0; i < 10; i++) {
                    var mid = (lo + hi) / 2;
                    fit.style.fontSize = mid + 'px';
                    if (fit.scrollHeight <= innerH && fit.scrollWidth <= innerW + 0.5) lo = mid;
                    else hi = mid;
                }
                fit.style.fontSize = lo + 'px';
            });
        </script></body></html>`;

        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        iframe.srcdoc = html;
        iframe.onload = () => {
            setTimeout(() => {
                iframe.contentWindow.print();
                setTimeout(() => {
                    try { document.body.removeChild(iframe); } catch (e) { /* 이미 제거됨 */ }
                }, 60000);
            }, 200);
        };

        if (onPrinted) onPrinted();
    };

    const pageCount = Math.ceil((safeStart + totalLabels) / totalCells);

    return (
        <div style={ms.overlay} onClick={onClose}>
            <div style={ms.modal} onClick={e => e.stopPropagation()}>
                <div style={ms.header}>
                    <h3 style={ms.title}>
                        🏷️ 라벨 인쇄
                        {items.length > 1 && (
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#6366f1', marginLeft: 8 }}>
                                환자 {items.length}명
                            </span>
                        )}
                    </h3>
                    <button onClick={onClose} style={ms.closeBtn}><X size={20} /></button>
                </div>

                <div style={ms.body}>
                    {/* 규격 */}
                    <div style={ms.section}>
                        <div style={ms.sectionTitle}>라벨지 규격</div>
                        <select value={preset} onChange={e => selectPreset(e.target.value)} style={ms.select}>
                            {Object.entries(PRESETS).map(([k, v]) => (
                                <option key={k} value={k}>{v.name}</option>
                            ))}
                        </select>
                        <div style={ms.geoGrid}>
                            {[
                                ['cols', '열'], ['rows', '행'], ['w', '라벨 너비(mm)'], ['h', '라벨 높이(mm)'],
                                ['ml', '왼쪽 여백(mm)'], ['mt', '위 여백(mm)'], ['gx', '가로 간격(mm)'], ['gy', '세로 간격(mm)'],
                            ].map(([key, label]) => (
                                <label key={key} style={ms.geoItem}>
                                    <span style={ms.geoLabel}>{label}</span>
                                    <input type="number" step="0.1" value={geo[key]}
                                        onChange={e => updateGeo(key, e.target.value)} style={ms.geoInput} />
                                </label>
                            ))}
                        </div>
                        <div style={ms.hint}>
                            시판 라벨지(폼텍 등)는 포장에 적힌 규격을 위 칸에 맞추면 됩니다. 첫 인쇄는 일반 A4에 시험 인쇄 후 라벨지에 겹쳐 보세요.
                        </div>
                    </div>

                    {/* 내용 선택 */}
                    <div style={ms.section}>
                        <div style={ms.sectionTitle}>라벨에 넣을 내용 (환자명은 항상 포함)</div>
                        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                            {[
                                ['clinic', '치과명'], ['prosthesis', '치식·보철종류'], ['shade', '쉐이드'], ['due', '납품요구일'],
                            ].map(([key, label]) => (
                                <label key={key} style={ms.checkLabel}>
                                    <input type="checkbox" checked={fields[key]}
                                        onChange={e => setFields(prev => ({ ...prev, [key]: e.target.checked }))} />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* 시작 칸 + 매수 */}
                    <div style={ms.section}>
                        <div style={ms.sectionTitle}>
                            시작 칸 선택 <span style={{ fontWeight: 400, color: '#94a3b8' }}>(쓰다 만 라벨지는 남은 칸을 클릭)</span>
                        </div>
                        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: `repeat(${geo.cols}, 44px)`,
                                gap: 3,
                            }}>
                                {Array.from({ length: totalCells }, (_, i) => {
                                    const used = i >= safeStart && i < safeStart + totalLabels;
                                    return (
                                        <div key={i} onClick={() => setStartCell(i)} style={{
                                            height: 26, borderRadius: 4, cursor: 'pointer',
                                            border: '1px solid ' + (used ? '#6366f1' : '#e2e8f0'),
                                            backgroundColor: used ? '#eef2ff' : '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 11, color: used ? '#6366f1' : '#cbd5e1', fontWeight: 600,
                                        }}>
                                            {i + 1}
                                        </div>
                                    );
                                })}
                            </div>
                            <div style={{ fontSize: 13, color: '#475569', fontWeight: 600 }}>
                                <label>
                                    {items.length > 1 ? '환자당 매수' : '매수'}
                                    <input type="number" min="1" value={copies}
                                        onChange={e => setCopies(e.target.value)}
                                        style={{ ...ms.geoInput, width: 64, marginLeft: 8 }} />
                                </label>
                                <div style={{ fontWeight: 400, color: '#94a3b8', marginTop: 6, lineHeight: 1.5 }}>
                                    총 라벨 {totalLabels}칸
                                    {pageCount > 1 && <><br />({pageCount}장에 나눠 인쇄)</>}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 미리보기 */}
                    <div style={ms.section}>
                        <div style={ms.sectionTitle}>
                            미리보기{items.length > 1 ? ` (${items.length}명 전체)` : ' (라벨 1칸)'}
                        </div>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: items.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                            gap: 8,
                        }}>
                            {items.map((item, idx) => (
                                <div key={idx} style={{
                                    border: '1px dashed #cbd5e1', borderRadius: 4, padding: 10,
                                    backgroundColor: '#fffef5', overflow: 'hidden',
                                }}>
                                    <div style={{ fontSize: 15, fontWeight: 700, color: '#000' }}>
                                        {item.patientName || '-'}
                                        {item.chartNumber && <span style={{ fontSize: 11, fontWeight: 400, color: '#333' }}> {item.chartNumber}</span>}
                                    </div>
                                    {fields.clinic && item.clinicName && (
                                        <div style={{ fontSize: 11.5, fontWeight: 600, color: '#000', marginTop: 2 }}>{item.clinicName}</div>
                                    )}
                                    {buildLines(item).map((l, i) => (
                                        <div key={i} style={{ fontSize: 11.5, color: '#000', lineHeight: 1.45 }}>{l}</div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div style={ms.footer}>
                    <button onClick={onClose} style={ms.cancelBtn}>닫기</button>
                    <button onClick={handlePrint} style={ms.printBtn}>
                        <Printer size={17} /> 라벨 인쇄
                    </button>
                </div>
            </div>
        </div>
    );
}

const ms = {
    overlay: {
        position: 'fixed', inset: 0, backgroundColor: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
    },
    modal: {
        width: 560, maxWidth: '100%', maxHeight: '90vh', backgroundColor: '#fff',
        borderRadius: 14, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
    },
    header: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', borderBottom: '1px solid #e2e8f0',
    },
    title: { margin: 0, fontSize: 17, fontWeight: 700, color: '#1e293b' },
    closeBtn: {
        background: 'none', border: 'none', cursor: 'pointer', color: '#64748b',
        display: 'flex', padding: 4,
    },
    body: { padding: 20, overflowY: 'auto' },
    section: { marginBottom: 22 },
    sectionTitle: { fontSize: 13.5, fontWeight: 700, color: '#334155', marginBottom: 8 },
    select: {
        width: '100%', padding: '9px 10px', fontSize: 14, borderRadius: 8,
        border: '1px solid #cbd5e1', marginBottom: 10, backgroundColor: '#fff',
    },
    geoGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 },
    geoItem: { display: 'flex', flexDirection: 'column', gap: 3 },
    geoLabel: { fontSize: 11, color: '#64748b', fontWeight: 600 },
    geoInput: {
        padding: '6px 8px', fontSize: 13, borderRadius: 6, border: '1px solid #cbd5e1', width: '100%',
        boxSizing: 'border-box',
    },
    hint: {
        marginTop: 10, padding: '8px 10px', backgroundColor: '#f8fafc', borderRadius: 6,
        fontSize: 12, color: '#64748b', lineHeight: 1.5,
    },
    checkLabel: {
        display: 'flex', alignItems: 'center', gap: 6, fontSize: 13.5, color: '#334155',
        fontWeight: 600, cursor: 'pointer',
    },
    footer: {
        display: 'flex', justifyContent: 'flex-end', gap: 10,
        padding: '14px 20px', borderTop: '1px solid #e2e8f0',
    },
    cancelBtn: {
        padding: '10px 18px', fontSize: 14, fontWeight: 600, borderRadius: 8,
        border: '1px solid #cbd5e1', backgroundColor: '#fff', color: '#475569', cursor: 'pointer',
    },
    printBtn: {
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
        fontSize: 14, fontWeight: 700, borderRadius: 8, border: 'none',
        backgroundColor: '#6366f1', color: '#fff', cursor: 'pointer',
    },
};

export default LabelPrintModal;
